import React from "react";
import {
  ActionButton,
  Actions,
  Container,
  Details,
  Message,
  SecondaryButton,
  Title,
} from "./AppErrorBoundary.styles";

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/**
 * Перехватывает ошибки рендера, чтобы приложение не падало в белый экран.
 *
 * Границы ошибок работают только на классовых компонентах — хука-аналога в
 * React нет, поэтому здесь класс, а не функция.
 */
class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Единственное место в приложении, где вывод в консоль оправдан:
    // иначе ошибка рендера потеряется совсем.
    if (process.env.NODE_ENV !== "production") {
      console.error("Ошибка рендера:", error, errorInfo.componentStack);
    }
  }

  private handleRetry = (): void => {
    this.setState({ error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <Container role="alert">
        <Title>Что-то пошло не так</Title>
        <Message>
          Раздел не удалось отобразить. Попробуйте повторить или обновите
          страницу — остальные разделы должны работать.
        </Message>
        <Actions>
          <ActionButton type="button" onClick={this.handleRetry}>
            Повторить
          </ActionButton>
          <SecondaryButton type="button" onClick={this.handleReload}>
            Обновить страницу
          </SecondaryButton>
        </Actions>
        {process.env.NODE_ENV !== "production" ? (
          <Details>
            <summary>Подробности для разработчика</summary>
            {error.message}
          </Details>
        ) : null}
      </Container>
    );
  }
}

export default AppErrorBoundary;
